import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import PrismWordmark from "./ui/PrismWordmark";
import { LoginPage } from "./pages/LoginPage";
import { TwoFactorChallengePage } from "./pages/TwoFactorChallengePage";
import { TwoFactorEnrollPage } from "./pages/TwoFactorEnrollPage";
import { AccountHomePage } from "./pages/AccountHomePage";
import { ServersPage } from "./pages/ServersPage";
import { DeviceApprovePage } from "./pages/DeviceApprovePage";
import { DevicesPage } from "./pages/DevicesPage";
import { InvitesPage } from "./pages/InvitesPage";
import { JoinPage } from "./pages/JoinPage";
import { SecurityPage } from "./pages/SecurityPage";
import { ActivityPage } from "./pages/ActivityPage";
import { DeleteAccountPage } from "./pages/DeleteAccountPage";
import { AccountDeletedPage } from "./pages/AccountDeletedPage";

const OPS_ORIGIN = "https://ops.prismstreaming.com";

function handoffToOps() {
  try {
    const raw = sessionStorage.getItem("prism-auth-session");
    if (!raw) return;
    const s = JSON.parse(raw) as { access_token: string; refresh_token: string };
    const frag = new URLSearchParams({
      access_token: s.access_token,
      refresh_token: s.refresh_token,
    }).toString();
    window.open(`${OPS_ORIGIN}/#${frag}`, "_blank", "noopener");
  } catch { /* if session missing, let the button silently no-op */ }
}

function Header() {
  const { state, clearSession } = useAuth();
  return (
    <div className="header">
      <Link to="/" aria-label="Prism — account" style={{ display: "inline-flex" }}>
        <PrismWordmark height={28} />
      </Link>
      {state.status === "authenticated" && (
        <nav className="nav">
          <NavLink to="/" end>Account</NavLink>
          <NavLink to="/servers">Installs</NavLink>
          <NavLink to="/devices">Devices</NavLink>
          <NavLink to="/invites">Invites</NavLink>
          <NavLink to="/security">Security</NavLink>
          <NavLink to="/activity">Activity</NavLink>
          <NavLink to="/device">Approve TV</NavLink>
          <button className="btn" onClick={handoffToOps}>Open Ops</button>
          <button className="btn" onClick={clearSession}>Sign out</button>
        </nav>
      )}
    </div>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { state } = useAuth();
  const location = useLocation();
  // Carry the originally-requested location through to /login (and, via
  // LoginPage, through 2FA if enrolled) so a signed-out visitor who hits a
  // deep link like /delete-account lands there after signing in instead of
  // on the account home page. See LoginPage/TwoFactorChallengePage for the
  // read side of this.
  if (state.status !== "authenticated") return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

export function App() {
  return (
    <div className="layout">
      <Header />
      <main className="main">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/2fa/challenge" element={<TwoFactorChallengePage />} />
          <Route path="/2fa/enroll" element={<RequireAuth><TwoFactorEnrollPage /></RequireAuth>} />
          <Route path="/" element={<RequireAuth><AccountHomePage /></RequireAuth>} />
          <Route path="/servers" element={<RequireAuth><ServersPage /></RequireAuth>} />
          <Route path="/devices" element={<RequireAuth><DevicesPage /></RequireAuth>} />
          <Route path="/invites" element={<RequireAuth><InvitesPage /></RequireAuth>} />
          <Route path="/security" element={<RequireAuth><SecurityPage /></RequireAuth>} />
          <Route path="/activity" element={<RequireAuth><ActivityPage /></RequireAuth>} />
          <Route path="/device" element={<RequireAuth><DeviceApprovePage /></RequireAuth>} />
          <Route path="/delete-account" element={<RequireAuth><DeleteAccountPage /></RequireAuth>} />
          <Route path="/account-deleted" element={<AccountDeletedPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
